type SearchFieldOption = { value?: string | null } | null;

type SearchFieldTda = {
    index?: string | null;
    addOnAttribute?: { optionValues?: SearchFieldOption[] | null } | null;
} | null;

/**
 * Resolves the allowed search field values for a list, preferring the backend search TDA over the
 * generated enum.
 *
 * The search TDA is the authority: its option list is per-tenant and can ship new values (e.g.
 * ADDRESS) before `schema.graphql` / `src/codegen/graphql.ts` are regenerated. Validating against
 * the generated enum alone meant a newly added backend field was stripped again right after the
 * TDA reconciliation added it, which fired a second, contradictory search request.
 *
 * `fallback` (the generated enum) is used until the TDA has loaded, and is deliberately kept: it
 * strips values the backend *removed* that are still persisted in localStorage or a bookmarked URL.
 *
 * Known limit: the TDA has not loaded on the very first render, so a cold deep-link carrying a
 * field that exists in the backend but not yet in the generated enum is still sanitized away.
 * Regenerating the schema remains the fix for that case.
 */
export const resolveValidQueryFields = (
    attributes: SearchFieldTda[] | null | undefined,
    fallback: string[]
): string[] => {
    const searchAttribute = attributes?.find((attribute) => attribute?.index === "search");
    const optionValues = (searchAttribute?.addOnAttribute?.optionValues ?? [])
        .map((option) => option?.value)
        .filter((value): value is string => !!value);

    return optionValues.length ? optionValues : fallback;
};
